import { computed, inject, Injectable } from '@angular/core';
import { UserInterface, UserServiceInterface } from '@techniek-team/common';
import { isDefined } from '@techniek-team/rxjs';
import { OidcSecurityService, UserDataResult } from 'angular-auth-oidc-client';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TT_AUTH_CONFIGS } from '../../auth.provider';
import { OidcOauthConfig } from '../../config/oidc-oauth.config';
import { LyceoUser } from '../../models/lyceo-user.model';
import { OAuthService } from '../oauth/oauth.service';
import { user as userSignal } from './user.signal';

@Injectable({
  providedIn: 'root',
})
export class UserService<L extends UserInterface = LyceoUser> implements UserServiceInterface {
  private readonly oidcSecurityService = inject(OidcSecurityService);

  private readonly configs = inject(TT_AUTH_CONFIGS);

  public readonly userModel = computed<L | undefined>(() => {
    const userData = this.oidcSecurityService.userData();
    return this.buildUserFromResponse(userData);
  });

  public readonly user = userSignal(this.userModel);

  public currentUser<K extends UserInterface = L>(configId?: string): Observable<K> {
    return this.oidcSecurityService.userData$.pipe(
      map((response) => this.buildUserFromResponse<K>(response, configId)),
      distinctUntilChanged(isEqual),
      isDefined<K>(),
    );
  }

  //eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  private buildUserFromResponse<K extends UserInterface = L>(
    response: UserDataResult,
    configId?: string,
  ): K | undefined {
    if (!response.userData || response.allUserData.length === 0) {
      return undefined;
    }

    let config: OidcOauthConfig<K> = this.configs[0];
    if (configId) {
      config = this.configs.find((item) => item.configId === configId) ?? this.configs[0];
    }
    if (response.userData && !configId) {
      return this.createModelFromResponse<K>(config, response.userData);
    }
    let data = response.allUserData.find((item) => item.configId === configId);
    if (!data) {
      data = response.allUserData[0];
    }

    return this.createModelFromResponse<K>(config, data.userData);
  }

  private createModelFromResponse<M extends UserInterface = L>(
    config: OidcOauthConfig<M>,
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
  ): M {
    try {
      //eslint-disable-next-line new-cap
      return new config.model(response);
    } catch (e) {
      return response as unknown as M;
    }
  }
}
